import { useCallback, useEffect, useState } from 'react';
import { ApiError, getUsers, type Member } from '@/lib/api';

/**
 * The caller's agency's members (USR-1).
 *
 * The same shape as `useSpaces`.
 *
 * **This is not authorization.** SEC-5 puts that in the API, which refuses a
 * non-admin caller whatever this hook returns. What comes back here decides
 * what the UI shows; it never decides what the user may do.
 */

export type UsersState =
  | { kind: 'loading' }
  /** An empty list is a loaded state: a one-admin agency is a real state (CON-7). */
  | { kind: 'loaded'; members: Member[] }
  /**
   * `status` carried for the same reason as in `useMe`: a 403 is permanent and
   * offers no retry, a 500 is worth one. `0` is network-level.
   */
  | { kind: 'error'; status: number };

export interface UseUsersResult {
  state: UsersState;
  /** After a failure. Clears the list back to `loading` first. */
  retry: () => void;
  /**
   * After a mutation — revoking, reactivating, changing the admin flag.
   *
   * Deliberately does **not** reset to `loading`, for the same reason
   * `useSpaces.refresh` does not: the rows on screen are still true while the
   * refetch is in flight, and blanking a table the user is looking at to
   * re-render almost the same rows a moment later reads as a glitch.
   */
  refresh: () => void;
}

export function useUsers(): UseUsersResult {
  const [state, setState] = useState<UsersState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    getUsers(controller.signal)
      .then(({ members }) => {
        setState({ kind: 'loaded', members });
      })
      .catch((error: unknown) => {
        // An abort is our own doing, not a failure.
        if (error instanceof Error && error.name === 'AbortError') return;
        setState({ kind: 'error', status: error instanceof ApiError ? error.status : 0 });
      });

    return () => {
      controller.abort();
    };
  }, [reloadToken]);

  const refresh = useCallback(() => {
    setReloadToken((current) => current + 1);
  }, []);

  // The loading reset belongs in the handler, not the effect: calling setState
  // in an effect body triggers a cascading render.
  const retry = useCallback(() => {
    setState({ kind: 'loading' });
    setReloadToken((current) => current + 1);
  }, []);

  return { state, retry, refresh };
}
